import {Component, EventEmitter, Input, Output, TemplateRef, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {TextFieldModule} from '@angular/cdk/text-field';
import {VoilaUserService} from '@services/voila-user.service';
import {ProductService} from '@services/product.service';
import {UUID} from '@util/types';
import Required from '@util/decorators/required';

@Component({
  selector: 'app-order-sample',
  templateUrl: './order-sample.component.html',
  styleUrls: ['./order-sample.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule, TextFieldModule],
})
export class OrderSampleComponent {
  @ViewChild('dialogTemplate') dialogTemplate: TemplateRef<unknown>;
  dialogRef?: MatDialogRef<unknown>;

  @Required @Input() productId: UUID;
  @Output() triggerSnackbar = new EventEmitter<boolean>();

  readonly ORDER_FIELDS = {
    COMPANY: 'company',
    NAME: 'name',
    STREET: 'street',
    ZIP: 'zip',
    CITY: 'city',
    COUNTRY: 'country',
    MESSAGE: 'message',
  };
  isSubmitting = false;
  hasSampleBeenRequested = false;
  dialogButtonText = 'Muster anfragen';

  form: FormGroup = this.fb.group({
    [this.ORDER_FIELDS.COMPANY]: ['', Validators.required],
    [this.ORDER_FIELDS.NAME]: ['', Validators.required],
    [this.ORDER_FIELDS.STREET]: ['', Validators.required],
    [this.ORDER_FIELDS.ZIP]: ['', Validators.required],
    [this.ORDER_FIELDS.CITY]: ['', Validators.required],
    [this.ORDER_FIELDS.COUNTRY]: ['', Validators.required],
    [this.ORDER_FIELDS.MESSAGE]: ['', [Validators.required, Validators.maxLength(2500)]],
  });

  constructor(
    private readonly userService: VoilaUserService,
    private readonly productService: ProductService,
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog,
  ) {}

  openDialog() {
    this.prefillForm();
    this.dialogRef = this.dialog.open(this.dialogTemplate, {width: '800px', autoFocus: false});
  }

  closeDialog() {
    this.dialogRef?.close();
  }

  private prefillForm() {
    const currentUser = this.userService.currentUser();
    this.form.patchValue({
      [this.ORDER_FIELDS.NAME]: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
    });
    if (this.isUserGettingDefaultMessage()) {
      this.form.patchValue({
        [this.ORDER_FIELDS.COMPANY]: 'Markant Deutschland GmbH',
        [this.ORDER_FIELDS.STREET]: 'Hanns-Martin-Schleyer-Str. 2',
        [this.ORDER_FIELDS.ZIP]: '77656',
        [this.ORDER_FIELDS.CITY]: 'Offenburg',
        [this.ORDER_FIELDS.COUNTRY]: 'DEUTSCHLAND',
        [this.ORDER_FIELDS.MESSAGE]: this.buildDefaultMessage(),
      });
    }
  }

  private buildDefaultMessage(): string {
    return `Hallo Start-up,

vielen Dank für Deine Bewerbung. Wir würden gerne erfahren wie Du auf unsere Plattform aufmerksam geworden bist und uns einen weitergehenden Eindruck von Deinen genannten Produkten verschaffen. Wir würden uns freuen, wenn Du uns hierfür an die Lieferadresse Muster (Tray pro Sorte) zukommen lässt.

Vielen Dank im Voraus und Grüße aus Offenburg.`;
  }

  isUserGettingDefaultMessage(): boolean {
    const currentUser = this.userService.currentUser();
    return currentUser.userPrivilege == 'FULL' && currentUser.userType == 'MA';
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;
    const message = this.buildPayloadMessage();
    this.productService.requestSample(this.productId, this.userService.currentUser().companyId, message).subscribe(() => {
      this.hasSampleBeenRequested = true;
      this.dialogButtonText = 'Muster angefragt';
      this.triggerSnackbar.emit(true);
      this.closeDialog();
      this.isSubmitting = false;
    });
  }

  private buildPayloadMessage(): string {
    const {company, name, street, zip, city, country, message} = this.form.value;
    const combined = [message, '', 'Lieferadresse:', `${company}`, `${name}`, `${street}`, `${zip} ${city}`, `${country}`].join('\n');
    return this.replaceLineBreaks(combined);
  }

  //Mailjet doesnt know \n, but accepts <br>-Tags as line breaks
  replaceLineBreaks(message: string) {
    return message.replace(/(?:\r\n|\r|\n)/g, '<br>');
  }
}
